import React, { useEffect, useState } from 'react';
import { Typography, Card, CardContent, CardHeader, CircularProgress, Box } from '@mui/material';
import timeEntryService from '../services/timeEntryService';
import taskEntryService from '../services/taskEntryService';
import errorEntryService from '../services/errorEntryService';
import authService from '../services/authService';

interface SummaryData {
  time_entries: any[];
  task_entries: any[];
  error_entries: any[];
}

const Dashboard: React.FC = () => {
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentUserRole = authService.getCurrentUser()?.role || '';

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      // In a real application, you would fetch data from your API
      // For now, we'll use the services to get mock data
      const timeEntries = await timeEntryService.getAll();
      const taskEntries = await taskEntryService.getAll();
      const errorEntries = await errorEntryService.getAll();
      
      setSummary({
        time_entries: timeEntries,
        task_entries: taskEntries,
        error_entries: errorEntries
      });
      setError(null);
    } catch (err) {
      setError('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
          <Card>
            <CardHeader title="Time Entries" />
            <CardContent>
              <Typography variant="h5">
                {summary?.time_entries.length || 0}
              </Typography>
              <Typography color="textSecondary">
                Total time entries
              </Typography>
            </CardContent>
          </Card>
        </Box>
        
        <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
          <Card>
            <CardHeader title="Task Entries" />
            <CardContent>
              <Typography variant="h5">
                {summary?.task_entries.length || 0}
              </Typography>
              <Typography color="textSecondary">
                Total task entries
              </Typography>
            </CardContent>
          </Card>
        </Box>
        
        {(currentUserRole === 'Admin' || currentUserRole === 'Manager') && (
          <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
            <Card>
              <CardHeader title="Error Entries" />
              <CardContent>
                <Typography variant="h5">
                  {summary?.error_entries.length || 0}
                </Typography>
                <Typography color="textSecondary">
                  Total error entries
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default Dashboard;